import { useCallback, useEffect, useState } from 'react';

import { safeLocalStorage } from '@spacefill/uikit/src/utils/storage';

const STORAGE_KEY = 'ai-chat-history';
const MAX_CONVERSATIONS = 50;
const RETENTION_DAYS = 7;
// Stay well under the 5MB localStorage limit shared with the rest of the app
const MAX_STORAGE_BYTES = 1024 * 1024; // 1024KB

function isWithinRetention(conv) {
    const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
    return (conv.updatedAt ?? conv.createdAt ?? 0) >= cutoff;
}

function loadConversations() {
    try {
        const raw = safeLocalStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return parsed.filter(isWithinRetention);
    } catch {
        return [];
    }
}

function saveConversations(conversations) {
    try {
        const eligible = conversations.filter(isWithinRetention);
        let serialized = JSON.stringify(eligible);
        if (new Blob([serialized]).size > MAX_STORAGE_BYTES) {
            // Drop oldest conversations until it fits
            const trimmed = [...eligible];
            while (trimmed.length > 1 && new Blob([JSON.stringify(trimmed)]).size > MAX_STORAGE_BYTES) {
                trimmed.pop();
            }
            serialized = JSON.stringify(trimmed);
        }
        safeLocalStorage.setItem(STORAGE_KEY, serialized);
        return eligible;
    } catch {
        return conversations;
    }
}

export function useAiChatHistory() {
    const [conversations, setConversations] = useState(loadConversations);

    useEffect(() => {
        setConversations(loadConversations());
    }, []);

    const saveConversation = useCallback((id, messages, ratings = {}) => {
        if (!messages.length) return;

        const title = messages.find((m) => m.role === 'user')?.content?.slice(0, 60) ?? '…';
        const preview = messages.find((m) => m.role === 'assistant')?.content?.slice(0, 80) ?? '';
        const now = Date.now();

        setConversations((prev) => {
            const existing = prev.findIndex((c) => c.id === id);
            let next;
            if (existing !== -1) {
                const updated = { ...prev[existing], title, preview, messages, ratings, updatedAt: now };
                next = [updated, ...prev.filter((c) => c.id !== id)];
            } else {
                const newConv = { id, title, preview, messages, ratings, createdAt: now, updatedAt: now };
                next = [newConv, ...prev].slice(0, MAX_CONVERSATIONS);
            }
            return saveConversations(next);
        });
    }, []);

    const deleteConversation = useCallback((id) => {
        setConversations((prev) => {
            const next = prev.filter((c) => c.id !== id);
            saveConversations(next);
            return next;
        });
    }, []);

    return { conversations, saveConversation, deleteConversation };
}
